import { useState } from "react";
import api from "@/lib/api";
import { Markdown } from "@/lib/markdown";
import { toast } from "sonner";
import { Languages, ArrowRightLeft, Loader2, Sparkles } from "lucide-react";

export default function Translator() {
  const [mode, setMode] = useState("to_plain");
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const example = mode === "to_plain"
    ? "We're raising a pre-seed round using a SAFE with a $5M cap to extend our runway and hit PMF before our Series A."
    : "We help small shops get more customers from Google and keep them coming back.";

  const translate = async () => {
    if (!text.trim()) return;
    setLoading(true); setResult("");
    try {
      const { data } = await api.post("/translator", { text, mode });
      setResult(data.result);
    } catch (e) { toast.error("Translation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="translator-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Business Patois Translator</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Decode the jargon. Or sound the part.</h1>
        <p className="text-slate-400 mt-2">Translate dense business-speak into plain English — or polish plain English into investor-ready language.</p>
      </div>

      <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1">
        <button data-testid="mode-to_plain" onClick={() => { setMode("to_plain"); setResult(""); }} className={`text-sm font-medium px-4 py-2 rounded-full transition-all ${mode === "to_plain" ? "bg-white text-black" : "text-slate-400"}`}>Business → Plain English</button>
        <button data-testid="mode-to_business" onClick={() => { setMode("to_business"); setResult(""); }} className={`text-sm font-medium px-4 py-2 rounded-full transition-all ${mode === "to_business" ? "bg-white text-black" : "text-slate-400"}`}>Plain → Business</button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="eco-card p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">{mode === "to_plain" ? "Business language" : "Plain English"}</span>
            <button onClick={() => setText(example)} className="text-xs text-[#4d8bff] hover:underline">Use example</button>
          </div>
          <textarea data-testid="translator-input" value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Paste your text here…" className="w-full bg-transparent text-white placeholder:text-slate-600 resize-none focus:outline-none" />
          <button data-testid="translator-btn" onClick={translate} disabled={loading || !text.trim()} className="btn-accent w-full mt-3">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><ArrowRightLeft size={16} /> Translate</>}
          </button>
        </div>
        <div className="eco-card p-5 min-h-[260px]">
          <span className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">{mode === "to_plain" ? "Plain English" : "Business language"}</span>
          <div className="mt-3" data-testid="translator-result">
            {loading ? <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={15} className="animate-spin" /> Translating…</div>
              : result ? <Markdown>{result}</Markdown>
              : <div className="flex flex-col items-center justify-center h-48 text-slate-600"><Languages size={28} className="mb-2" /><p className="text-sm">Your translation appears here.</p></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
